import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ArrowLeft, ArrowRight, ListChecks } from 'lucide-react';
import ToastContainer from 'components/_UI/ToastsContainer';
import useToast from 'hooks/useToast';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { useEvent } from 'context/events/Event';
import customFieldsApi from 'api/requests/customFields';
import CustomFieldsSection from '../detail/additional/sections/CustomFieldsSection';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export default function EventCustomFieldsStep({
    onSuccess = () => false,
    goBackFormStep = () => false,
}) {
    const { event } = useEvent();
    const { toast, toasts } = useToast();
    const [content, setContent] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getCustomFields();
    }, [event?.slug]);

    async function getCustomFields() {
        if (!event?.slug) return;

        setLoading(true);
        try {
            const { data } = await customFieldsApi.get(event.slug);
            setContent(data?.data || []);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    }

    return (
        <Container>
            <ToastContainer toasts={toasts} />

            <BackRow
                onClick={() => goBackFormStep(2)}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && goBackFormStep(2)}
            >
                <BackIconWrap>
                    <ArrowLeft size={18} color={panelAccent(colors.secondaryPurple)} />
                </BackIconWrap>
                <BackLabel>Voltar pros ingressos</BackLabel>
            </BackRow>

            <IntroBanner>
                <IntroIconWrap>
                    <ListChecks size={26} color={colors.neutralLight} />
                </IntroIconWrap>
                <IntroText>
                    <IntroTitleRow>
                        <IntroTitle>Campos adicionais</IntroTitle>
                        <OptionalBadge>Opcional</OptionalBadge>
                    </IntroTitleRow>
                    <IntroHint>
                        Colete informações extras de quem compra ingresso — documento,
                        tamanho de camiseta, restrição alimentar, o que precisar. Dá pra
                        pular agora e configurar depois em "Adicionais".
                    </IntroHint>
                </IntroText>
            </IntroBanner>

            <CustomFieldsSection
                data={content}
                loading={loading}
                onBack={() => goBackFormStep(2)}
                showBackRow={false}
                toast={toast}
                onSuccess={getCustomFields}
            />

            <BottomBar>
                <BottomBarContent>
                    <SubmitButton onClick={() => onSuccess(event)}>
                        {content?.length ? 'Avançar' : 'Pular por enquanto'}
                        <ArrowRight size={16} />
                    </SubmitButton>
                </BottomBarContent>
            </BottomBar>
        </Container>
    );
}

const Container = styled.div`
    overflow-x: auto;
    padding-bottom: 100px;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 12px 6px 6px;
    margin-bottom: 18px;
    width: fit-content;
    border-radius: 10px;
    outline: none;
    transition: background 0.15s ease;

    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { box-shadow: 0 0 0 2px ${panelAccentAlpha(colors.secondaryPurple, '44')}; }
`;

const BackIconWrap = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BackLabel = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 500;
    color: ${colors.nightDark};
`;

const IntroBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 20px;
    margin-bottom: 24px;
    border-radius: 16px;
    background: linear-gradient(135deg, ${panelAccent(colors.occasionalPurple)} 0%, ${panelAccent(colors.secondaryPurple)} 100%);
    box-shadow: 0 8px 24px ${panelAccentAlpha(colors.occasionalPurple, '33')};

    @media screen and (min-width: ${breakpoints.md}) {
        align-items: center;
    }
`;

const IntroIconWrap = styled.div`
    width: 52px;
    height: 52px;
    min-width: 52px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const IntroText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const IntroTitleRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
`;

const IntroTitle = styled.h2`
    font-size: 22px;
    font-weight: 700;
    color: #fff;
    margin: 0;
`;

const OptionalBadge = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #fff;
    background: rgba(255, 255, 255, 0.22);
    padding: 3px 10px;
    border-radius: 20px;
`;

const IntroHint = styled.p`
    max-width: 640px;
    font-size: 14px;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.92);
    margin: 0;
`;

const BottomBar = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #fff;
    border-top: 1px solid ${colors.night}15;
    box-shadow: 0 -4px 20px ${colors.night}0A;
    z-index: 10;
    padding: 14px 24px;

    @media screen and (min-width: ${breakpoints.md}) {
        width: 75%;
    }
`;

const BottomBarContent = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    max-width: 500px;
    margin: 0 auto;
`;

const SubmitButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 1;
    padding: 12px 20px;
    border: none;
    border-radius: 10px;
    background: ${panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${panelAccent(colors.secondaryPurple)};
    }
`;
